import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, ShieldCheck, MessageCircle, RotateCcw, ArrowRight, Star, Quote } from "lucide-react";
import { getProducts, getCategories } from "../lib/api";
import { CONTACT, waLink } from "../lib/constants";
import ProductCard from "../components/ProductCard";
import GoldDivider from "../components/GoldDivider";
import Reveal from "../components/Reveal";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c774f08e-7103-4f8c-9839-096394067636/images/723fe1729e98a0c3f5473e8a7f9596628f4e4a7974b9301a5f09dfa2e1aaf4c3.jpeg";

const REASSURANCE = [
  { icon: Truck, title: "Livraison rapide", text: "Partout à Abidjan & en Côte d'Ivoire" },
  { icon: ShieldCheck, title: "Paiement sécurisé", text: "Mobile Money, carte ou à la livraison" },
  { icon: MessageCircle, title: "Commande WhatsApp", text: "Un conseiller à votre écoute" },
  { icon: RotateCcw, title: "Retours faciles", text: "Satisfait ou remboursé sous 7 jours" },
];

const TESTIMONIALS = [
  { name: "Aïcha K.", city: "Cocody", text: "Service impeccable et produits de qualité. J'ai commandé via WhatsApp, livré le lendemain !", rating: 5 },
  { name: "Mariam T.", city: "Yopougon", text: "Les finitions dorées sont magnifiques. On sent le côté premium, je recommande vivement.", rating: 5 },
  { name: "Fatou D.", city: "Marcory", text: "Enfin une boutique qui propose des articles utiles ET élégants pour la famille. Bravo MaxiChoix.", rating: 5 },
];

export default function Home() {
  const [newProducts, setNewProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    getProducts({ featured: "new", sort: "newest" }).then((d) => setNewProducts(d.slice(0, 4)));
    getProducts({ featured: "bestseller" }).then((d) => setBestSellers(d.slice(0, 4)));
    getCategories().then(setCategories);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero-noir relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-center min-h-[80vh] py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="gold-divider justify-start mb-6">
              <span className="text-[#F4E5A1] text-xs uppercase tracking-[0.3em]">Boutique premium famille</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] text-white mb-6">
              Le <span className="gold-text">meilleur choix</span><br /> pour toute la famille
            </h1>
            <p className="text-neutral-300 text-lg max-w-md mb-8 leading-relaxed">
              Des articles pratiques, utiles et tendance — sélectionnés avec soin et livrés partout en Côte d'Ivoire.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/boutique" className="btn-gold px-8 py-4 rounded-sm text-base" data-testid="hero-shop-btn">
                Découvrir la boutique <ArrowRight size={18} />
              </Link>
              <a
                href={waLink("Bonjour MaxiChoix ! Je souhaite être conseillé(e) pour une commande.")}
                target="_blank" rel="noreferrer"
                className="btn-outline-gold px-8 py-4 rounded-sm text-base !text-white !border-[#B8860B]"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={18} /> Commander via WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#B8860B]/30 to-transparent blur-2xl rounded-full" />
            <img src={HERO_IMG} alt="Sélection premium MaxiChoix" className="relative rounded-lg shadow-2xl w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="border-b border-neutral-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6 py-10">
          {REASSURANCE.map((r) => (
            <div key={r.title} className="flex items-center gap-4" data-testid="reassurance-item">
              <div className="w-12 h-12 shrink-0 rounded-full border border-[#B8860B]/30 flex items-center justify-center text-[#B8860B]">
                <r.icon size={22} strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-display text-lg text-[#0A0A0A] leading-tight">{r.title}</p>
                <p className="text-xs text-neutral-500">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* UNIVERS PRODUITS */}
      <section className="py-20 px-4 md:px-8 bg-[#FBF9F4]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-4">
              <span className="text-xs uppercase tracking-[0.3em] text-[#B8860B]">Nos univers</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-center text-[#0A0A0A] mb-4">
              Explorez par catégorie
            </h2>
            <GoldDivider className="mb-14" />
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat, i) => (
              <Reveal key={cat.slug} delay={i * 0.08}>
                <Link
                  to={`/boutique?category=${cat.slug}`}
                  className="group block relative overflow-hidden rounded-lg bg-[#0A0A0A] p-8 h-52 flex flex-col justify-end"
                  data-testid={`category-card-${cat.slug}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
                  <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.25em] text-[#F4E5A1]/70">
                    {cat.universe}
                  </div>
                  <div className="relative">
                    <h3 className="font-display text-2xl text-white group-hover:text-[#F4E5A1] transition mb-1">{cat.name}</h3>
                    <p className="text-sm text-neutral-400 mb-3">{cat.tagline}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-[#B8860B] group-hover:gap-3 transition-all">
                      Découvrir <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAUTES */}
      <ProductStrip title="Nouveautés" subtitle="Fraîchement arrivés" products={newProducts} link="/boutique?category=nouveautes" testid="new-products" />

      {/* BANNER */}
      <section className="hero-noir py-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <Quote className="mx-auto text-[#B8860B] mb-4" size={40} />
            <p className="font-display text-3xl md:text-4xl text-white leading-snug mb-6">
              « Le luxe accessible, au service du quotidien de votre famille. »
            </p>
            <Link to="/boutique?featured=bestseller" className="btn-gold px-8 py-4 rounded-sm">
              Voir les meilleures ventes
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MEILLEURES VENTES */}
      <ProductStrip title="Meilleures ventes" subtitle="Les préférés de nos clientes" products={bestSellers} link="/boutique?featured=bestseller" testid="bestseller-products" light />

      {/* TEMOIGNAGES */}
      <section className="py-20 px-4 md:px-8 bg-[#FBF9F4]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="block text-center text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2">Elles nous font confiance</span>
            <h2 className="font-display text-4xl md:text-5xl text-center text-[#0A0A0A] mb-4">Avis de nos clientes</h2>
            <GoldDivider className="mb-14" />
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div className="bg-white p-8 border border-neutral-100 h-full" data-testid="testimonial-card">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <p className="text-neutral-700 leading-relaxed mb-6 italic">“{t.text}”</p>
                  <div className="font-display text-lg text-[#0A0A0A]">{t.name}</div>
                  <div className="text-xs text-[#B8860B] uppercase tracking-widest">{t.city}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHATSAPP CTA */}
      <section className="py-20 px-4 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto bg-[#0A0A0A] rounded-2xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#B8860B]/20 blur-3xl rounded-full" />
          <MessageCircle className="mx-auto text-[#25D366] mb-4" size={44} />
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Commandez en 2 clics sur WhatsApp</h2>
          <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
            Une question sur un produit ? Besoin de conseils ? Écrivez-nous, nous répondons rapidement.
          </p>
          <a
            href={waLink("Bonjour MaxiChoix ! Je souhaite passer une commande.")}
            target="_blank" rel="noreferrer"
            className="btn-gold px-8 py-4 rounded-sm text-base inline-flex"
            data-testid="cta-whatsapp"
          >
            <MessageCircle size={18} /> Écrire sur WhatsApp
          </a>
          <p className="mt-6 text-sm text-neutral-500">
            {CONTACT.phoneIntl} • {CONTACT.email}
          </p>
        </div>
      </section>
    </div>
  );
}

function ProductStrip({ title, subtitle, products, link, testid, light }) {
  return (
    <section className={`py-20 px-4 md:px-8 ${light ? "bg-white" : "bg-white"}`} data-testid={testid}>
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="block text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2">{subtitle}</span>
              <h2 className="font-display text-4xl md:text-5xl text-[#0A0A0A]">{title}</h2>
            </div>
            <Link to={link} className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#0A0A0A] link-underline">
              Tout voir <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
        <div className="mt-8 text-center md:hidden">
          <Link to={link} className="btn-outline-gold px-6 py-3 rounded-sm inline-flex">
            Tout voir <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
